import { type FC, Fragment } from 'react';

import type { CategoryNode } from '@/api/address-book/types';
import { LockedCategoryGate, LockedCategoryMark } from '@/features/address-book/locked-category';
import { Checkbox, theme, Typography } from '@/shared/design-system';

import {
  coveredCategoryIds,
  includedWithLabel,
  SELECTED_CATEGORIES_LABEL,
} from '../../category-scope';
import { TreeRow, TreeRows } from './styled';

export type CategoryScopeTreeProps = {
  categories: readonly CategoryNode[];
  picked: ReadonlySet<string>;
  editable: boolean;
  onToggle: (_id: string) => void;
};

export const CategoryScopeTree: FC<CategoryScopeTreeProps> = ({
  categories,
  picked,
  editable,
  onToggle,
}) => {
  const covered = coveredCategoryIds(categories, picked);

  const toggle = (node: CategoryNode) => {
    if (!node.locked) onToggle(node.id);
  };

  const rows = (nodes: readonly CategoryNode[], depth: number) =>
    nodes.map((node) => {
      const coveredBy = covered.get(node.id);
      const id = `scope-category-${node.id}`;
      const reasonId = `${id}-reason`;

      return (
        <Fragment key={node.id}>
          <TreeRow $depth={depth}>
            <LockedCategoryGate locked={node.locked}>
              <Checkbox
                id={id}
                size="sm"
                checked={picked.has(node.id) || coveredBy !== undefined}
                disabled={!editable || node.locked || coveredBy !== undefined}
                aria-describedby={coveredBy === undefined ? undefined : reasonId}
                onCheckedChange={() => toggle(node)}
              />
            </LockedCategoryGate>
            <Typography
              as="label"
              id={`${id}-label`}
              htmlFor={id}
              variant="body3"
              color={theme.colors.text.primary}
              fontWeight={depth === 0 ? 600 : 400}
            >
              {node.name}
            </Typography>
            {node.locked && <LockedCategoryMark />}
            {coveredBy && (
              <Typography id={reasonId} variant="body4" color={theme.colors.text.tertiary}>
                {includedWithLabel(coveredBy)}
              </Typography>
            )}
          </TreeRow>
          {rows(node.children, depth + 1)}
        </Fragment>
      );
    });

  return (
    <TreeRows role="group" aria-label={SELECTED_CATEGORIES_LABEL}>
      {rows(categories, 0)}
    </TreeRows>
  );
};
